<script lang="ts">
  /**
   * The navy header every admin screen opens with, so the admin area reads as
   * the same product as the quality area rather than a plain form page. The
   * shape is deliberately the one in quality/SiteList.svelte: an orange
   * kicker, a tight title, an optional pill action, and an optional strip of
   * figures across the bottom.
   */
  import type { Snippet } from 'svelte';

  interface Stat {
    /** The figure itself, already formatted — this component never rounds. */
    value: string;
    label: string;
    /** Marks a figure that is bad news, so a loss does not read as a win. */
    tone?: 'plain' | 'bad';
  }

  interface Props {
    kicker: string;
    title: string;
    stats?: readonly Stat[];
    /** The pill button on the right, and the row of links under the title. */
    action?: Snippet;
    links?: Snippet;
  }

  const { kicker, title, stats, action, links }: Props = $props();
</script>

<section class="hero">
  <div class="hero__inner">
    <div class="hero__head">
      <p class="hero__kicker">{kicker}</p>
      <h1 class="hero__title">{title}</h1>
    </div>
    {#if action}
      {@render action()}
    {/if}
  </div>

  <!--
    The links sit OUTSIDE hero__inner, not inside the head beside the title.
    Inside, they shared their width with the action pill: on a phone the pill
    took its ~200px of a ~340px hero and left the chips ~120px, so each one
    took a row of its own and five links cost five rows. Out here they have
    the whole width and wrap two or three to a line.
  -->
  {#if links}
    <div class="hero__links">{@render links()}</div>
  {/if}

  {#if stats && stats.length > 0}
    <div class="hero__stats" style="--cols: {stats.length}" aria-label="Summary">
      {#each stats as stat (stat.label)}
        <div>
          <strong class:bad={stat.tone === 'bad'}>{stat.value}</strong>
          <span>{stat.label}</span>
        </div>
      {/each}
    </div>
  {/if}
</section>

<style>
  .hero {
    background: linear-gradient(160deg, var(--brand-dark) 0%, var(--brand) 70%);
    color: #fff;
    border-radius: var(--radius-lg);
    padding: 1.1rem 1.1rem 0.9rem;
    margin-bottom: 0.9rem;
    box-shadow: var(--shadow);
  }
  .hero__inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
    /* Wraps so a narrow screen drops the action pill below the title rather
       than squeezing the title into two lines beside it. */
    flex-wrap: wrap;
  }
  .hero__head {
    min-width: 0;
    /* Takes the row when there is room, and keeps 12rem before the pill is
       allowed to wrap under it. */
    flex: 1 1 12rem;
  }
  .hero__kicker {
    margin: 0 0 0.15rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--accent);
  }
  .hero__title {
    margin: 0;
    font-size: 1.8rem;
    letter-spacing: -0.02em;
    line-height: 1.05;
  }

  .hero__links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.75rem;
  }

  .hero__stats {
    display: grid;
    /* auto-fit at every width, not only on a phone: four figures in four hard
       columns overflow as soon as one of them reads $12,345.67. */
    grid-template-columns: repeat(auto-fit, minmax(7.5rem, 1fr));
    gap: 0.6rem 0.9rem;
    margin-top: 1rem;
    padding-top: 0.8rem;
    border-top: 1px solid rgb(255 255 255 / 0.18);
  }
  .hero__stats div { display: grid; gap: 0.1rem; min-width: 0; }
  .hero__stats strong {
    /* Shrinks with the column so a long figure stays on one line. */
    font-size: clamp(1.05rem, 3.6vw, 1.35rem);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }
  /* Warm amber rather than a true red: on this navy a red reads as an error
     banner, and a negative month is a figure, not a fault. */
  .hero__stats strong.bad { color: #ffb4a2; }
  .hero__stats span {
    font-size: 0.74rem;
    opacity: 0.8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  @media (max-width: 26rem) {
    .hero__title { font-size: 1.5rem; }
    .hero__stats { grid-template-columns: repeat(auto-fit, minmax(6.5rem, 1fr)); }
  }

  @media (min-width: 48rem) {
    .hero__stats { grid-template-columns: repeat(var(--cols, 3), 1fr); }
  }
</style>
